/**
 * Small helpers the steps share.
 */

import { receiptLines, type ReceiptLine } from '../grounding.js';
import { readingFrom } from '../resolve.js';
import type { Candidate, FieldKey, ModelTransport, Ports, ReadingState } from '../types.js';

export function transportOf(ports: Ports): ModelTransport {
  if (!ports.transport) throw new Error('no model transport');
  return ports.transport;
}

/** Candidates as short phrases for the trace: `total 20,00 | 19,00`. */
export function summarize(candidates: Partial<Record<FieldKey, Candidate[]>>): string[] {
  return Object.entries(candidates).map(([field, list]) => {
    const values = (list ?? []).map((candidate) => {
      if (Array.isArray(candidate.value)) return `[${candidate.value.length}]`;
      if (candidate.value && typeof candidate.value === 'object') {
        const name = (candidate.value as Record<string, unknown>).name;
        return typeof name === 'string' ? name : '{…}';
      }
      return String(candidate.value);
    });
    return `${field} ${values.join(' | ')}`;
  });
}

/** The reading so far: what `resolve` last chose, as `normalizeExtraction` takes it. */
export function currentReading(state: Readonly<ReadingState>): Record<string, unknown> {
  return readingFrom(state.resolved);
}

/** The OCR lines' text, as numbered in a prompt (from 1). */
export function lineTexts(state: Readonly<ReadingState>): string[] {
  return (state.input.lines ?? []).map((line) => line.text);
}

/** The OCR lines as the grounding reads them, numbered as a prompt numbers them. */
export function groundingLines(state: Readonly<ReadingState>): ReceiptLine[] {
  const texts = lineTexts(state);
  return receiptLines(texts.length > 0 ? texts.join('\n') : (state.input.text ?? ''));
}
